const UTM_PARAM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
];

const CLICK_ID_PARAM_KEYS = ["gclid", "gbraid", "wbraid", "msclkid", "fbclid"];

const ATTRIBUTION_PARAM_KEYS = [...UTM_PARAM_KEYS, ...CLICK_ID_PARAM_KEYS];

const STORAGE_KEY = "sml_attribution";

const getStoredAttribution = () => {
  if (typeof window === "undefined") return null;

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const setStoredAttribution = (data) => {
  if (typeof window === "undefined") return;

  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    // ignore storage failures (private browsing, quota, etc.)
  }
};

// Ad click-ID/UTM data is only persisted with marketing consent granted -
// mirrors the "Interest-Based Advertising Cookies" category in our own
// cookie policy. Reuses the same consent record CookieConsent writes.
const hasMarketingConsent = () => {
  if (typeof window === "undefined") return false;

  try {
    const consent = JSON.parse(window.localStorage.getItem("sml_cc") ?? "{}");
    return Boolean(consent?.marketing);
  } catch {
    return false;
  }
};

// Landing page/referrer are basic first-touch usage data, not ad targeting -
// mirrors the "Analytics Cookies" category, so either consent unlocks them.
const hasAnalyticsConsent = () => {
  if (typeof window === "undefined") return false;

  try {
    const consent = JSON.parse(window.localStorage.getItem("sml_cc") ?? "{}");
    return Boolean(consent?.analytics);
  } catch {
    return false;
  }
};

const readAttributionParams = (urlString) => {
  if (typeof window === "undefined" || !urlString) return {};

  let params;

  try {
    params = new URL(urlString, window.location.origin).searchParams;
  } catch {
    return {};
  }

  const incoming = {};

  ATTRIBUTION_PARAM_KEYS.forEach((key) => {
    const value = params.get(key);
    if (value) {
      incoming[key] = value;
    }
  });

  return incoming;
};

const readUrlAttributionParams = () => {
  if (typeof window === "undefined") return {};

  return readAttributionParams(window.location.href);
};

// Reads GA4's own _ga cookie (format GA1.<version>.<clientIdPart1>.<clientIdPart2>)
// so leads can be cross-referenced against GA4 without any extra script.
const getGaClientId = () => {
  if (typeof document === "undefined") return null;

  const match = document.cookie.match(/(?:^|;\s*)_ga=([^;]+)/);
  if (!match) return null;

  const parts = match[1].split(".");
  return parts.length >= 4 ? parts.slice(-2).join(".") : null;
};

const getGoogleAdsClickId = () => {
  if (typeof document === "undefined") return null;

  const match = document.cookie.match(/(?:^|;\s*)_gcl_aw=([^;]+)/);
  if (!match) return null;

  try {
    const parts = decodeURIComponent(match[1]).split(".");
    return parts.length >= 3 && parts[2] ? parts[2] : null;
  } catch {
    return null;
  }
};

// Call on every page load/route change. Captures UTM + ad click-ID params
// from the URL (last-touch wins, requires marketing consent) plus the true
// landing page + referrer on first-ever visit (requires analytics OR
// marketing consent - it's plain usage data, not ad targeting), and persists
// them so they're still available if the lead converts on a later page/visit.
export const captureAttribution = () => {
  if (typeof window === "undefined") return;

  const incoming = readUrlAttributionParams();
  const hasIncoming = Object.keys(incoming).length > 0;
  const existing = getStoredAttribution();

  const canCaptureMarketing = hasMarketingConsent();
  const canCaptureContext = canCaptureMarketing || hasAnalyticsConsent();

  if (!canCaptureContext && !canCaptureMarketing) {
    return;
  }

  if (!hasIncoming && existing && !canCaptureMarketing) {
    return;
  }

  const landingPageParams = readAttributionParams(existing?.landing_page);

  const isFirstTouch = hasIncoming || !existing;

  const next = ATTRIBUTION_PARAM_KEYS.reduce(
    (acc, key) => ({
      ...acc,
      [key]: canCaptureMarketing
        ? (incoming[key] ?? existing?.[key] ?? landingPageParams[key] ?? null)
        : (existing?.[key] ?? null),
    }),
    {
      landing_page: !canCaptureContext
        ? (existing?.landing_page ?? null)
        : isFirstTouch
          ? `${window.location.pathname}${window.location.search}`
          : existing.landing_page,
      referrer: !canCaptureContext
        ? (existing?.referrer ?? null)
        : isFirstTouch
          ? document.referrer || null
          : existing.referrer,
    },
  );

  setStoredAttribution(next);
};

// Builds the full attribution payload to submit with a lead. Falls back to
// live URL params/referrer if nothing was persisted (e.g. no marketing
// consent yet), so every lead still carries whatever context is available.
export const getAttributionForSubmission = () => {
  const stored = getStoredAttribution() || {};
  const liveIncoming = readUrlAttributionParams();
  const landingPageParams = readAttributionParams(stored.landing_page);
  const cookieGclid = hasMarketingConsent() ? getGoogleAdsClickId() : null;

  const attribution = ATTRIBUTION_PARAM_KEYS.reduce(
    (acc, key) => ({
      ...acc,
      [key]:
        liveIncoming[key] ??
        stored[key] ??
        landingPageParams[key] ??
        (key === "gclid" ? cookieGclid : null),
    }),
    {},
  );

  const landingPage =
    stored.landing_page ??
    (typeof window !== "undefined"
      ? `${window.location.pathname}${window.location.search}`
      : null);

  if (landingPage) {
    try {
      const parsedLandingPage = new URL(landingPage, window.location.origin);

      ATTRIBUTION_PARAM_KEYS.forEach((key) => {
        if (attribution[key] && !parsedLandingPage.searchParams.has(key)) {
          parsedLandingPage.searchParams.set(key, attribution[key]);
        }
      });

      attribution.landing_page = landingPage.startsWith("/")
        ? `${parsedLandingPage.pathname}${parsedLandingPage.search}${parsedLandingPage.hash}`
        : parsedLandingPage.toString();
    } catch {
      attribution.landing_page = landingPage;
    }
  } else {
    attribution.landing_page = null;
  }

  attribution.referrer =
    stored.referrer ??
    (typeof document !== "undefined" ? document.referrer || null : null);
  attribution.form_page_url =
    typeof window !== "undefined" ? window.location.href : null;
  attribution.ga_client_id = getGaClientId();
  attribution.submitted_at = new Date().toISOString();

  return attribution;
};
